import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { isEmpty, omitBy } from "lodash";
import errorHandler from "Utils/error-handler";
import { LoadingOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import {
  AlertBox,
  BasicModal,
  Button,
  TextInput,
} from "@life-line-lab/components-react";
import { OrderChargesSchema } from "./order-charges-validation";
import "./order-charges.scss";
import { Row, Col, Spin } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";
import useAccessControlList from "Hooks/access-control.hook";
import { useSession } from "Hooks/index";
import { ParcelTypeEnums } from "Data/order";

interface OrderChargesProp {
  type: string;
  orderDetails: any;
  onSubmit?: any;
  hideModal?: any;
  onDeleteCharges?: any;
  isLoading?: boolean;
}

const AmountBreakdown = ({
  orderCharges,
  type,
  showAddChargesOption,
  handleActionClick,
}) => {
  const { t } = useTranslation();
  const { roleTypes } = useSession();

  const isRoleCustomer = roleTypes == "CUSTOMER-SUPER_ADMIN" && true;

  // base charges are inclusive of delivery type and service type for customers
  const baseCharges = parseFloat(orderCharges?.baseCharges);
  const cumulativeBaseCharges = isRoleCustomer
    ? orderCharges?.additionalCharges?.reduce((base, charge) => {
        if (charge.type == "DELIVERY_TYPE" || charge.type == "SERVICE_TYPE") {
          return base + parseFloat(charge.amount);
        } else {
          return base;
        }
      }, 0) + baseCharges
    : baseCharges;

  // exclude delivery type and service type for additional charges for customers
  const additionalCharges = isRoleCustomer
    ? orderCharges?.additionalCharges?.filter(
        (charge) =>
          charge.type != "DELIVERY_TYPE" && charge.type != "SERVICE_TYPE",
      )
    : orderCharges?.additionalCharges;

  const BreakdownTable = () => {
    return (
      <Col span={24} xl={12}>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Row gutter={[8, 8]}>
              <Col span={8}>
                <div className="dm-charge-light-label">
                  {t("label.baseCharges")}:
                </div>
              </Col>

              <Col flex="auto">
                <Row>
                  <Col flex="auto">
                    <div className="dm-charge-amt">
                      RM{" "}
                      {cumulativeBaseCharges
                        ? cumulativeBaseCharges.toFixed(2)
                        : 0.0}
                    </div>
                  </Col>
                </Row>

                {!isEmpty(orderCharges.childOrderBaseCharge) &&
                  orderCharges.childOrderBaseCharge?.map(
                    (charge: string, index: number) => (
                      <Row key={index}>
                        <Col span={16}>{`Package ${index + 1}`}</Col>
                        <Col flex="auto">
                          <div className="dm-charge-amt">
                            {parseFloat(charge) < 0 && "-"}
                            RM {parseFloat(charge?.replace("-", "")).toFixed(2)}
                          </div>
                        </Col>
                      </Row>
                    ),
                  )}
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Row gutter={[8, 8]}>
              <Col span={8}>
                <div className="dm-charge-light-label">
                  {t("label.additionalCharges")}:
                </div>
              </Col>
              <Col flex="auto">
                {additionalCharges?.map((charge, index) => (
                  <Row key={index}>
                    <Col span={16}>{charge?.name}</Col>
                    <Col flex="auto">
                      <div className="dm-charge-amt">
                        {parseFloat(charge?.amount) < 0 && "-"} RM{" "}
                        {parseFloat(charge?.amount.replace("-", "")).toFixed(2)}
                      </div>
                    </Col>
                  </Row>
                ))}
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <div className="dm-divider"></div>
          </Col>
          <Col span={24}>
            <Row>
              <Col span={6}>
                <div className="dm-charge-light-label">{t("label.total")}</div>
              </Col>
              <Col flex="auto">
                <div className="dm-charge-amt dm-charge-emp-amt">
                  RM {parseFloat(orderCharges?.totalCharges).toFixed(2)}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    );
  };

  return (
    <div className="dm-put-content">
      <Row gutter={[16, 24]}>
        <Col span={16} xl={8}>
          <label className="title"> {t("label.amountBreakdown")} </label>
        </Col>
        <Col span={8} xl={4}>
          <div className="dm-charge-btn">
            {type == "actual" && showAddChargesOption ? (
              <Button
                type="primary"
                label={t("action.addCharges")}
                onClick={() => {
                  handleActionClick();
                }}
                icon={<PlusCircleOutlined />}
              />
            ) : null}
          </div>
        </Col>
        <Col span={12}></Col>
        {orderCharges && <BreakdownTable />}
        <Col span={12}></Col>
        <Col span={12}>
          <div className="dm-info dm-help">
            <QuestionCircleOutlined style={{ fontSize: "16px" }} />
            <div>{t("txt.chargesInstruction")}</div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

const OrderCharges: React.FC<OrderChargesProp> = ({
  type,
  orderDetails,
  onSubmit,
  hideModal,
  onDeleteCharges,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { roleTypes } = useSession();
  const initialInput = {
    chargeName: "",
    chargeAmount: "",
  };
  const initialError = {};
  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [orderCharges, setOrderCharges] = useState<any>();
  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "Order_Management",
  });

  const showUpdateOption = permission.rights.includes("UPDATE");
  const showAddChargesOption = permission.rights.includes("UPDATE_ADD_CHARGES");

  useEffect(() => {
    const isEstimated = type === "estimated";
    const isSplitShipment = orderDetails.orderType === ParcelTypeEnums.SPLIT;
    const childOrderBaseCharge: string[] = [];

    const orderAdditionalCharge = isEstimated
      ? orderDetails.estimatedAdditionalCharges
      : orderDetails.additionalCharges;

    let orderBaseCharges = isEstimated
      ? orderDetails.estimatedBaseCharges
      : orderDetails.baseCharges;

    const orderTotalCharges = isEstimated
      ? orderDetails.estimatedTotalCharges
      : orderDetails.totalCharges;

    // split shipments will display total AND separate base charges for each package
    // whereas the other order types display only 1
    if (isSplitShipment) {
      orderBaseCharges = isEstimated
        ? orderDetails.estimatedTotalBaseCharges
        : orderDetails.totalBaseCharges;

      orderDetails.childrenOrdersData
        .sort((a, b) => parseInt(a.runningNumber) - parseInt(b.runningNumber))
        .map((childOrder: any) =>
          childOrderBaseCharge.push(childOrder.baseCharges),
        );
    }

    setOrderCharges({
      additionalCharges: orderAdditionalCharge,
      baseCharges: orderBaseCharges,
      totalCharges: orderTotalCharges,
      childOrderBaseCharge: childOrderBaseCharge,
    });
  }, [orderDetails]);

  const initialModal: any = {
    open: false,
  };
  const [modal, setModal] = useState<any>(initialModal);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleResetModalState = () => {
    setModal(initialModal);
  };

  const handleActionClick = () => {
    setModal({ open: true });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleSubmit = () => {
    try {
      const valInput = {
        ...input,
      };
      const value: any = OrderChargesSchema.validateSync(
        omitBy(valInput, isNil),
        {
          strict: false,
          abortEarly: true,
          stripUnknown: true,
        },
      );
      onSubmit(value);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`) }),
      });
    }
  };

  useEffect(() => {
    handleResetModalState();
  }, [hideModal]);

  return (
    <div className="order-details">
      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
      ) : (
        <>
          <AmountBreakdown
            orderCharges={orderCharges}
            type={type}
            showAddChargesOption={showAddChargesOption}
            handleActionClick={handleActionClick}
          />
          <BasicModal
            className="modal-new-charge"
            isOpen={modal.open}
            isClosable={true}
            isMaskClosable={false}
            onClose={() => {
              handleResetModalState();
            }}
          >
            <div className="modal-content-title">
              {t("label.addNewCharge").toLocaleUpperCase()}
            </div>
            <div className="modal-content-text">
              {t("label.order").toLocaleUpperCase()}
              {/* orderNumber or code */}
              {orderDetails.orderCode}
              <br />
            </div>
            <div className="modal-content-text">
              <TextInput
                isRequired={true}
                label={t("label.chargeName")}
                placeholder={t("label.enterHere")}
                name="chargeName"
                onChange={handleChange}
              />
              <AlertBox text={error.chargeName} />
              <br />
              <TextInput
                isRequired={true}
                label={t("label.chargeAmount")}
                placeholder={t("label.enterHere")}
                name="chargeAmount"
                onChange={handleChange}
              />
              <AlertBox text={error.chargeAmount} />
            </div>

            <div className="mt-30 right modal-end-button">
              <Button
                type={"primary"}
                label={t("action.confirm")}
                onClick={() => {
                  handleSubmit();
                }}
              />
              <Button
                className="cancel-btn"
                type={"default"}
                label={t("action.cancel")}
                onClick={() => {
                  handleResetModalState();
                }}
              />
            </div>
          </BasicModal>
        </>
      )}
    </div>
  );
};

export default OrderCharges;
